import {
  BadgeIndianRupee,
  Bell,
  CalendarRange,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  ReceiptText,
  Settings,
  ShoppingBag,
  Undo2,
  User,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { LoadingState } from '@/components/common/LoadingState';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { queryKeys } from '@/lib/query-client';
import { PORTAL_ROUTES } from '@/routes/paths';
import { portalNotificationsService } from '@/services/notifications.service';
import { usePortalAuthStore } from '@/store/portal-auth.store';

/**
 * The customer portal shell (Phase 6).
 *
 * Deliberately **not** the ERP's `AppLayout`. That shell renders the module
 * sidebar from the shared registry, which is the internal navigation — the one
 * thing the brief is most explicit that a customer must never see. Reusing it
 * and hiding items would leave the internal nav one conditional away from
 * appearing.
 *
 * So this is a small, self-contained shell: a top bar with the customer's own
 * name, a horizontal nav of the seven things they can look at, and a collapsible
 * drawer on a phone. Nothing here reads the module registry, the permission
 * catalog or the ERP auth store, so no internal concept can leak into it.
 *
 * It reads as a customer-facing application rather than an admin tool: light
 * surface, wide gutters, large readable figures, one column on a phone.
 */

interface PortalNavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** `end` on the overview only: every other path is a prefix of nothing. */
  end?: boolean;
  /** Shows the unread-message count beside the label (Phase 7). */
  badge?: boolean;
}

const NAV: PortalNavItem[] = [
  { to: PORTAL_ROUTES.dashboard, label: 'Overview', icon: LayoutDashboard, end: true },
  { to: PORTAL_ROUTES.quotations, label: 'Quotations', icon: FileText },
  { to: PORTAL_ROUTES.sales, label: 'Purchases', icon: ShoppingBag },
  { to: PORTAL_ROUTES.payments, label: 'Payments', icon: ReceiptText },
  { to: PORTAL_ROUTES.rentals, label: 'Rentals', icon: CalendarRange },
  { to: PORTAL_ROUTES.buybacks, label: 'Buybacks', icon: Undo2 },
  // Phase 7. `badge` marks the one entry that carries an unread count; the
  // layout resolves the number rather than the entry hard-coding a query, so a
  // second badged entry later needs no new plumbing.
  { to: PORTAL_ROUTES.notifications, label: 'Messages', icon: Bell, badge: true },
  { to: PORTAL_ROUTES.profile, label: 'My details', icon: User },
  { to: PORTAL_ROUTES.preferences, label: 'Contact settings', icon: Settings },
];

export const PortalLayout = () => {
  const navigate = useNavigate();
  const customer = usePortalAuthStore((state) => state.customer);
  const logout = usePortalAuthStore((state) => state.logout);
  const [menuOpen, setMenuOpen] = useState(false);

  /**
   * The unread count for the Messages badge (Phase 7).
   *
   * Fetched once and refreshed when a mutation invalidates the portal keys —
   * no polling timer. A customer's message count changes when the showroom
   * does something, which is not often enough to justify a request a minute
   * from every open tab.
   */
  const unread = useQuery({
    queryKey: queryKeys.portal.notificationsUnread,
    queryFn: () => portalNotificationsService.unreadCount(),
    staleTime: 60_000,
  });
  const unreadCount = unread.data?.unread ?? 0;

  const signOut = async () => {
    await logout();
    navigate(PORTAL_ROUTES.login, { replace: true });
  };

  const navContent = (item: PortalNavItem) => (
    <>
      <item.icon className="size-4 shrink-0" />
      {item.label}
      {item.badge && unreadCount > 0 ? (
        <span className="ml-auto inline-flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-4 text-primary-foreground">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      ) : null}
    </>
  );

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
      isActive
        ? 'bg-primary/10 font-medium text-primary'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
    );

  return (
    <div className="min-h-[100dvh] bg-muted/30">
      <header className="sticky top-0 z-30 border-b border-border bg-background">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted md:hidden"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-tight">
                {customer?.organization.name ?? 'Customer portal'}
              </p>
              <p className="truncate text-2xs text-muted-foreground">
                {customer ? `${customer.displayName} · ${customer.code}` : 'Customer portal'}
              </p>
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={() => void signOut()}>
            <LogOut />
            <span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>

        {/* Horizontal nav on tablet and up. */}
        <nav className="mx-auto hidden max-w-5xl gap-1 overflow-x-auto px-4 pb-2 md:flex">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
              {navContent(item)}
            </NavLink>
          ))}
        </nav>

        {/* Drawer on a phone. Closes on selection, so a tap never leaves it open. */}
        {menuOpen ? (
          <nav className="border-t border-border px-4 py-2 md:hidden">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={linkClass}
                onClick={() => setMenuOpen(false)}
              >
                {navContent(item)}
              </NavLink>
            ))}
          </nav>
        ) : null}
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        <Suspense fallback={<LoadingState variant="page" label="Loading…" />}>
          <Outlet />
        </Suspense>
      </main>

      <footer className="mx-auto max-w-5xl px-4 pb-8 pt-2">
        <p className="text-2xs text-muted-foreground">
          Questions about anything here? Contact {customer?.organization.name ?? 'the showroom'}{' '}
          directly. Amounts shown are as recorded on your documents.
        </p>
      </footer>
    </div>
  );
};

/** The sign-in shell — the same light surface, without the nav. */
export const PortalAuthLayout = () => (
  <div className="flex min-h-[100dvh] items-center justify-center bg-muted/30 px-4 py-10">
    <div className="w-full max-w-sm">
      <Suspense fallback={<LoadingState variant="page" label="Loading…" />}>
        <Outlet />
      </Suspense>
    </div>
  </div>
);

/** A page heading, used by every portal screen so they share one rhythm. */
export const PortalPage = ({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="space-y-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold leading-tight">{title}</h1>
        {description ? (
          <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
    {children}
  </div>
);

/** A card. The portal's one container, so screens do not each invent a box. */
export const PortalCard = ({
  title,
  description,
  actions,
  className,
  children,
}: {
  title?: string;
  description?: string;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) => (
  <section className={cn('rounded-lg border border-border bg-background', className)}>
    {title ? (
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold leading-tight">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
    ) : null}
    <div className="p-4">{children}</div>
  </section>
);

/** A label/value row. Stacks on a phone rather than squeezing into two columns. */
export const PortalField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex flex-col gap-0.5 border-b border-border py-2 last:border-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-sm sm:text-right">{value || '—'}</span>
  </div>
);

/**
 * The empty state.
 *
 * Every portal list has one, and they say what the customer can expect rather
 * than "no data". A blank screen with a raw API shape reads as a broken site to
 * someone who did not build it.
 */
export const PortalEmpty = ({ title, body }: { title: string; body: string }) => (
  <div className="flex flex-col items-center gap-1.5 px-4 py-12 text-center">
    <BadgeIndianRupee className="size-8 text-muted-foreground/40" />
    <p className="text-sm font-medium">{title}</p>
    <p className="max-w-sm text-xs text-muted-foreground">{body}</p>
  </div>
);
